import type { ElementTransformer, TextMatchTransformer, Transformer } from "@lexical/markdown";
import type { ElementNode, LexicalNode } from "lexical";
import { $isParagraphNode, ParagraphNode } from "lexical";
import { getImageDimensions } from "../../../../../utils/resize-image";
import { serviceURL } from "../../../../../utils/service-url";
import { $isButtonListNode, ButtonListNode } from "../../nodes/button-list-node";
import { $isDesignHuddleNode, DesignHuddleNode } from "../../nodes/design-huddle-node";
import { $isDividerNode, DividerNode } from "../../nodes/divider-node";
import { DynamicContentToolbarNode } from "../../nodes/dynamic-content/dynamic-content-toolbar-node";
import { $isImageNode, ImageNode } from "../../nodes/image-node";
import { $isMergeTagV2Node, MergeTagV2Node } from "../../nodes/merge-tag-node-v2";
import { $isVideoNode } from "../../nodes/video-node";

export const NoReplace = {
  replace: () => {},
  regExp: /(?=a)(?!a)/,
  importRegExp: /(?=a)(?!a)/,
  trigger: "",
};

const PARAGRAPH_NODE: ElementTransformer = {
  type: "element",
  ...NoReplace,
  dependencies: [ParagraphNode],
  export(node: LexicalNode, traverseChildren: (node: ElementNode) => string) {
    if (!$isParagraphNode(node)) {
      return null;
    }
    return JSON.stringify({ type: "paragraph", text: traverseChildren(node) });
  },
};

const IMAGE_NODE = (channel: "slack" | "teams"): ElementTransformer => ({
  type: "element",
  ...NoReplace,
  dependencies: [ImageNode],
  export(node: LexicalNode) {
    if (!$isImageNode(node)) {
      return null;
    }

    return JSON.stringify({
      type: "image",
      url: node.__url,
      size:
        channel === "teams" && !node.__url.startsWith(serviceURL("images"))
          ? getImageDimensions(node.__proportionOfMaxWidth, node.__aspect, 580)
          : undefined,
    });
  },
});

const DESIGN_HUDDLE_NODE = (channel: "slack" | "teams"): ElementTransformer => ({
  type: "element",
  ...NoReplace,
  dependencies: [DesignHuddleNode],
  export(node: LexicalNode) {
    if (!$isDesignHuddleNode(node) || !node.__url) {
      return null;
    }

    const needsResizing = channel === "teams" && !node.__customisations;
    if (node.__contentType === "video" && node.getHref()) {
      return JSON.stringify({
        type: "video",
        thumbnailUrl: node.__url,
        videoUrl: node.getHref(),
        size: needsResizing
          ? getImageDimensions(node.__proportionOfMaxWidth, node.__aspect, 580)
          : undefined,
        mediaEngineUrl: channel === "slack" ? node.__slackAppVideoUrl : undefined,
        title: channel === "slack" ? node.__title : undefined,
      });
    }

    return JSON.stringify({
      type: "image",
      url: node.__url,
      size: needsResizing
        ? getImageDimensions(node.__proportionOfMaxWidth, node.__aspect, 580)
        : undefined,
    });
  },
});

const VIDEO_NODE = (channel: "slack" | "teams"): ElementTransformer => ({
  type: "element",
  ...NoReplace,
  dependencies: [ImageNode],
  export(node: LexicalNode) {
    if (!$isVideoNode(node)) {
      return null;
    }

    const needsResizing = channel === "teams";
    return JSON.stringify({
      type: "video",
      videoUrl: node.__videoUrl,
      thumbnailUrl: node.__thumbnailUrl,
      size: needsResizing
        ? getImageDimensions(node.__proportionOfMaxWidth, node.__aspect, 580)
        : undefined,
      mediaEngineUrl: channel === "slack" ? node.__videoUrl : undefined,
      title: channel === "slack" ? node.__title : undefined,
    });
  },
});

const BUTTON_LIST_NODE: ElementTransformer = {
  type: "element",
  ...NoReplace,
  dependencies: [ButtonListNode],
  export(node: LexicalNode) {
    if (!$isButtonListNode(node)) {
      return null;
    }
    return JSON.stringify(node.getChatBlock());
  },
};

const SLACK_DIVIDER_NODE: ElementTransformer = {
  type: "element",
  ...NoReplace,
  dependencies: [DividerNode],
  export(node: LexicalNode) {
    if (!$isDividerNode(node)) {
      return null;
    }
    return JSON.stringify({ type: "divider" });
  },
};

const TEAMS_DIVIDER_NODE: ElementTransformer = {
  type: "element",
  ...NoReplace,
  dependencies: [DividerNode],
  export(node: LexicalNode) {
    if (!$isDividerNode(node)) {
      return null;
    }
    return JSON.stringify({ type: "paragraph", text: "---" });
  },
};

const DYNAMIC_CONTENT_TOOLBAR_NODE: ElementTransformer = {
  type: "element",
  ...NoReplace,
  dependencies: [DynamicContentToolbarNode],
  export() {
    return null;
  },
};

const MERGE_TAG_NODE: TextMatchTransformer = {
  type: "text-match",
  ...NoReplace,
  dependencies: [MergeTagV2Node],
  export(
    node: LexicalNode,
    _,
    exportFormat: (node: MergeTagV2Node, textContent: string) => string,
  ) {
    if (!$isMergeTagV2Node(node)) {
      return null;
    }

    return exportFormat(node, `{{${node.__text}}}`);
  },
};

export const ELEMENT_TRANSFORMERS = (channel: "teams" | "slack"): Transformer[] => {
  return [
    PARAGRAPH_NODE,
    IMAGE_NODE(channel),
    DESIGN_HUDDLE_NODE(channel),
    VIDEO_NODE(channel),
    BUTTON_LIST_NODE,
    channel === "slack" ? SLACK_DIVIDER_NODE : TEAMS_DIVIDER_NODE,
    MERGE_TAG_NODE,
    DYNAMIC_CONTENT_TOOLBAR_NODE,
  ];
};
